<div class="app-main__inner">

    <div class="app-page-title">
        <div class="page-title-wrapper">
            <div class="page-title-heading">
                <div class="page-title-icon">
                    <i class="pe-7s-paper-plane icon-gradient bg-happy-fisher"></i>
                </div>
                <div>
                    Trigger Campaign
                    <div class="page-title-subheading">
                        Select a saved campaign, pick a recipient list, and send.
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Result alert -->
    <div *ngIf="resultMessage" class="alert mb-3 py-2 px-3"
        [ngClass]="resultSuccess ? 'alert-success' : 'alert-danger'">
        <i [ngClass]="resultSuccess ? 'pe-7s-check' : 'pe-7s-attention'" class="me-1"></i>
        {{ resultMessage }}
    </div>

    <!-- Step 1: Select Campaign -->
    <div class="card mb-3">
        <div class="card-header d-flex align-items-center gap-2">
            <span class="step-badge">1</span>
            <span class="fw-semibold">Select Campaign</span>
        </div>
        <div class="card-body">
            <div *ngIf="isLoadingCampaigns" class="text-muted small">
                <span class="spinner-border spinner-border-sm me-1"></span> Loading campaigns...
            </div>
            <div *ngIf="!isLoadingCampaigns && savedCampaigns.length === 0" class="alert alert-warning py-2 px-3 mb-0">
                No saved campaigns yet. Go to <strong>Campaign Creation</strong> to create one first.
            </div>
            <div *ngIf="!isLoadingCampaigns && savedCampaigns.length > 0" class="row g-3 align-items-start">

                <!-- Left: selector + meta -->
                <div [class]="selectedCampaign ? 'col-md-5' : 'col-md-6'">
                    <label class="form-label fw-semibold">Campaign</label>
                    <select class="form-select form-select-lg"
                        [(ngModel)]="selectedCampaignId"
                        (ngModelChange)="onCampaignSelect($event)">
                        <option value="">— Select a campaign —</option>
                        <option *ngFor="let c of savedCampaigns" [value]="c._id">
                            {{ c.campaignName }}
                        </option>
                    </select>

                    <div *ngIf="selectedCampaign" class="border rounded p-3 bg-light mt-3">
                        <div class="mb-1 small"><span class="text-muted">Template:</span> <strong>{{ selectedCampaign.templateName }}</strong></div>
                        <div class="mb-1 small"><span class="text-muted">Type:</span> {{ selectedCampaign.templateType }}</div>
                        <div *ngIf="selectedCampaign.outerVariable" class="mb-1 small">
                            <span class="text-muted">Outer variable:</span>
                            <code class="ms-1">{{ selectedCampaign.outerVariable }}</code>
                        </div>
                        <div *ngIf="selectedCampaign.cardVariables.length" class="small">
                            <span class="text-muted">Card variables:</span>
                            <span *ngFor="let v of selectedCampaign.cardVariables; let i = index"
                                class="badge bg-secondary-subtle text-secondary border ms-1">
                                Card {{ i+1 }}: {{ v || '—' }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Right: WhatsApp preview -->
                <div *ngIf="selectedCampaign" class="col-md-7">
                    <label class="form-label fw-semibold">Preview</label>

                    <div *ngIf="isLoadingPreview" class="text-center py-5 text-muted">
                        <span class="spinner-border spinner-border-sm me-2"></span> Loading preview...
                    </div>

                    <div *ngIf="!isLoadingPreview && previewError" class="alert alert-danger py-2 px-3 small">
                        {{ previewError }}
                    </div>

                    <div *ngIf="!isLoadingPreview && campaignPreview" class="wa-mock mx-auto">
                        <div class="wa-mock-header">
                            <div class="wa-mock-avatar"></div>
                            <span class="wa-mock-name">Vintech Electronic</span>
                        </div>
                        <div class="wa-mock-body">
                            <div class="wa-msg-bubble">
                                <div class="wa-msg-text">
                                    {{ previewText(campaignPreview.outerBodyText, campaignPreview.outerVariable) }}
                                </div>
                                <div class="wa-carousel-strip">
                                    <div *ngFor="let img of campaignPreview.cardImages; let i = index" class="wa-carousel-card">
                                        <div class="wa-card-img-wrap">
                                            <img *ngIf="img" [src]="img" class="wa-card-img" />
                                            <div *ngIf="!img" class="wa-card-img-placeholder">
                                                <i class="pe-7s-photo"></i>
                                                <span>Card {{ i + 1 }}</span>
                                            </div>
                                        </div>
                                        <div class="wa-card-body-text">
                                            {{ previewText(campaignPreview.cardBodyTexts[i] || '', campaignPreview.cardVariables[i]) }}
                                        </div>
                                        <div class="wa-card-btns">
                                            <div *ngFor="let btn of previewButtons(i)" class="wa-btn">
                                                {{ btn || '(empty)' }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="wa-msg-time">Now · ✓✓</div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>

    <!-- Step 2: Recipients -->
    <div class="card mb-3">
        <div class="card-header d-flex align-items-center gap-2">
            <span class="step-badge">2</span>
            <span class="fw-semibold">Recipients</span>
        </div>
        <div class="card-body">

            <!-- Mode toggle -->
            <div class="recipient-mode-toggle mb-3">
                <label class="recipient-mode-option" [class.active]="recipientMode === 'group'">
                    <input type="radio" name="recipientMode" value="group"
                        [(ngModel)]="recipientMode" (ngModelChange)="onRecipientModeChange()" />
                    <i class="pe-7s-albums me-1"></i> Recipient Group
                </label>
                <label class="recipient-mode-option" [class.active]="recipientMode === 'users'">
                    <input type="radio" name="recipientMode" value="users"
                        [(ngModel)]="recipientMode" (ngModelChange)="onRecipientModeChange()" />
                    <i class="pe-7s-user me-1"></i> Select Users
                </label>
            </div>

            <!-- Group mode -->
            <ng-container *ngIf="recipientMode === 'group'">
                <div *ngIf="isLoadingLists" class="text-muted small">
                    <span class="spinner-border spinner-border-sm me-1"></span> Loading recipient lists...
                </div>
                <div *ngIf="!isLoadingLists && recipientLists.length === 0" class="alert alert-warning py-2 px-3 mb-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <span>No recipient lists yet.</span>
                    <a routerLink="/campaign/recipients" class="btn btn-sm btn-warning">
                        <i class="pe-7s-users me-1"></i>Create a Recipient List
                    </a>
                </div>
                <div *ngIf="!isLoadingLists && recipientLists.length > 0" class="row g-3 align-items-start">
                    <div class="col-md-6">
                        <label class="form-label fw-semibold">Recipient Group List</label>
                        <select class="form-select form-select-lg"
                            [(ngModel)]="selectedListId"
                            (ngModelChange)="onListSelect($event)">
                            <option value="">— Select a recipient list —</option>
                            <option *ngFor="let l of recipientLists" [value]="l._id">
                                {{ l.listName }} ({{ l.customerCount }} recipient{{ l.customerCount === 1 ? '' : 's' }})
                            </option>
                        </select>
                        <div *ngIf="selectedList" class="mt-2 d-flex align-items-center gap-2 flex-wrap">
                            <span class="badge bg-primary-subtle text-primary border border-primary-subtle fs-6 px-3 py-2">
                                <i class="pe-7s-users me-1"></i>
                                {{ selectedList.customerCount }} recipient{{ selectedList.customerCount === 1 ? '' : 's' }}
                            </span>
                            <span class="text-muted small">Created {{ selectedList.createdAt | date:'dd MMM yyyy' }}</span>
                            <button type="button" class="btn btn-sm btn-outline-primary" (click)="showListMembers()">
                                <i class="pe-7s-look me-1"></i>View Members
                            </button>
                        </div>
                    </div>
                    <div class="col-md-6 d-flex align-items-end">
                        <a routerLink="/campaign/recipients" class="btn btn-outline-secondary btn-sm">
                            <i class="pe-7s-users me-1"></i>Manage Recipient Lists
                        </a>
                    </div>
                </div>
            </ng-container>

            <!-- Select Users mode -->
            <ng-container *ngIf="recipientMode === 'users'">
                <div *ngIf="isLoadingUsers" class="text-muted small">
                    <span class="spinner-border spinner-border-sm me-1"></span> Loading users...
                </div>
                <ng-container *ngIf="!isLoadingUsers">
                    <!-- Search + select-all bar -->
                    <div class="d-flex gap-2 align-items-center mb-2">
                        <input type="text" class="form-control form-control-sm"
                            placeholder="Search by name or WhatsApp number…"
                            [(ngModel)]="userSearch" style="max-width:300px;" />
                        <button type="button" class="btn btn-sm btn-outline-secondary"
                            (click)="toggleAllFiltered()">
                            {{ allFilteredSelected ? 'Deselect All' : 'Select All' }}
                        </button>
                        <span *ngIf="selectedUsersCount > 0" class="badge bg-primary ms-1">
                            {{ selectedUsersCount }} selected
                        </span>
                    </div>

                    <div *ngIf="allUsers.length === 0" class="text-muted small fst-italic">No users found in User Master.</div>

                    <!-- User checkbox list -->
                    <div class="user-pick-list" *ngIf="allUsers.length > 0">
                        <div *ngIf="filteredUsers.length === 0" class="text-muted small fst-italic p-2">No users match your search.</div>
                        <label *ngFor="let u of filteredUsers; trackBy: trackByIndex"
                            class="user-pick-row"
                            [class.selected]="selectedUserIds.has(u._id)">
                            <input type="checkbox"
                                [checked]="selectedUserIds.has(u._id)"
                                (change)="toggleUser(u._id)" />
                            <span class="user-pick-name">
                                {{ u.name || (u.firstName || u.lastName ? ((u.firstName || '') + ' ' + (u.lastName || '')).trim() : '—') }}
                            </span>
                            <code class="user-pick-wa">{{ u.whatsAppNo }}</code>
                        </label>
                    </div>
                </ng-container>
            </ng-container>

        </div>
    </div>

    <!-- Trigger bar -->
    <div class="d-flex justify-content-between align-items-center mb-4 px-1">
        <div *ngIf="validationHint && !isTriggering" class="validation-hint">
            <i class="pe-7s-info me-1"></i> {{ validationHint }}
        </div>
        <div class="ms-auto">
            <button class="btn btn-success btn-lg px-5" (click)="trigger()" [disabled]="!isValid || isTriggering">
                <span *ngIf="isTriggering">
                    <span class="spinner-border spinner-border-sm me-2"></span>Sending...
                </span>
                <span *ngIf="!isTriggering">
                    <i class="pe-7s-paper-plane me-2"></i>Trigger Campaign
                </span>
            </button>
        </div>
    </div>

    <!-- Past Runs -->
    <div class="card">
        <div class="card-header d-flex align-items-center gap-2">
            <i class="header-icon pe-7s-clock icon-gradient bg-happy-fisher"></i>
            <span class="fw-semibold">Trigger History</span>
            <span class="badge bg-secondary rounded-pill ms-1">{{ runs.length }}</span>
        </div>
        <div class="card-body p-0">
            <div *ngIf="isLoadingRuns" class="text-center p-4 text-muted">
                <span class="spinner-border spinner-border-sm me-2"></span> Loading...
            </div>
            <div *ngIf="!isLoadingRuns && runs.length === 0" class="text-center p-4 text-muted">
                No triggers yet. Select a campaign and recipient list above to send your first one.
            </div>
            <vio-table-v2
                *ngIf="!isLoadingRuns && runs.length > 0"
                [tableData]="runsForTable"
                [tableConfig]="runsTableConfig">
            </vio-table-v2>
        </div>
    </div>

</div>


<!-- Delivery Modal -->
<div class="modal fade" id="triggerDeliveryModal" tabindex="-1" aria-hidden="true" #deliveryModal>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <div class="d-flex align-items-center gap-2">
                    <i class="pe-7s-graph1 icon-gradient bg-tempting-azure" style="font-size:1.4rem;"></i>
                    <h5 class="modal-title mb-0">Delivery Metrics</h5>
                    <span class="text-muted fw-normal" style="font-size:0.85rem;">— {{ deliveryRunName }}</span>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body" style="max-height:70vh;overflow-y:auto;">
                <div *ngIf="isLoadingDelivery" class="text-center py-5 text-muted">
                    <span class="spinner-border spinner-border-sm me-2"></span> Loading...
                </div>
                <div *ngIf="!isLoadingDelivery && !deliveryData" class="text-center py-5 text-muted">
                    No delivery data found.
                </div>
                <ng-container *ngIf="!isLoadingDelivery && deliveryData">
                    <div class="d-flex gap-3 flex-wrap mb-4">
                        <div class="delivery-stat"><div class="delivery-stat__count text-secondary">{{ deliveryData.summary.total }}</div><div class="delivery-stat__label">Total</div></div>
                        <div class="delivery-stat"><div class="delivery-stat__count text-primary">{{ deliveryData.summary.sent }}</div><div class="delivery-stat__label">Sent</div></div>
                        <div class="delivery-stat"><div class="delivery-stat__count text-success">{{ deliveryData.summary.delivered }}</div><div class="delivery-stat__label">Delivered</div></div>
                        <div class="delivery-stat"><div class="delivery-stat__count text-info">{{ deliveryData.summary.read }}</div><div class="delivery-stat__label">Read</div></div>
                        <div class="delivery-stat"><div class="delivery-stat__count text-danger">{{ deliveryData.summary.failed }}</div><div class="delivery-stat__label">Failed</div></div>
                    </div>
                    <div *ngIf="deliveryData.logs.length === 0" class="text-center text-muted py-4">No log entries yet.</div>
                    <div class="table-responsive" *ngIf="deliveryData.logs.length > 0">
                        <table class="table table-sm table-hover align-middle mb-0">
                            <thead class="table-light">
                                <tr><th>Name</th><th>WhatsApp No.</th><th>Status</th><th>Delivered At</th><th>Read At</th></tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let log of deliveryData.logs">
                                    <td>{{ log.recipientName }}</td>
                                    <td>{{ log.recipientNo }}</td>
                                    <td>
                                        <span class="badge"
                                            [ngClass]="{
                                                'bg-secondary': log.status===20,
                                                'bg-primary':   log.status===30,
                                                'bg-success':   log.status===40,
                                                'bg-danger':    log.status===10
                                            }">{{ statusLabel(log.status) }}</span>
                                        <ng-container *ngIf="log.status===10">
                                            <div class="text-danger small mt-1" style="max-width:260px;line-height:1.3;">
                                                <span *ngIf="log.failedErrorCode" class="fw-semibold">Code {{ log.failedErrorCode }}: </span>{{ log.failedReason || 'Delivery failed' }}
                                            </div>
                                            <div *ngIf="log.failedErrorDetail" class="text-muted" style="font-size:0.72rem;max-width:260px;line-height:1.3;">
                                                {{ log.failedErrorDetail }}
                                            </div>
                                        </ng-container>
                                    </td>
                                    <td>{{ log.deliveredAt ? (log.deliveredAt | date:'dd MMM h:mm a') : '—' }}</td>
                                    <td>{{ log.readAt ? (log.readAt | date:'dd MMM h:mm a') : '—' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ng-container>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
        </div>
    </div>
</div>

<!-- Engagement Modal -->
<div class="modal fade" id="triggerEngagementModal" tabindex="-1" aria-hidden="true" #engagementModal>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <div class="d-flex align-items-center gap-2">
                    <i class="pe-7s-like2 icon-gradient bg-happy-fisher" style="font-size:1.4rem;"></i>
                    <h5 class="modal-title mb-0">Engagement</h5>
                    <span class="text-muted fw-normal" style="font-size:0.85rem;">— {{ engagementRunName }}</span>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body" style="max-height:70vh;overflow-y:auto;">
                <div *ngIf="isLoadingEngagement" class="text-center py-5 text-muted">
                    <span class="spinner-border spinner-border-sm me-2"></span> Loading...
                </div>
                <div *ngIf="!isLoadingEngagement && !engagementData" class="text-center py-5 text-muted">
                    No engagement data found.
                </div>
                <ng-container *ngIf="!isLoadingEngagement && engagementData">
                    <div class="d-flex gap-3 flex-wrap mb-4">
                        <div class="delivery-stat"><div class="delivery-stat__count text-secondary">{{ engagementData.summary.total }}</div><div class="delivery-stat__label">Responses</div></div>
                        <div class="delivery-stat"><div class="delivery-stat__count text-success">{{ engagementData.summary.interested }}</div><div class="delivery-stat__label">Interested</div></div>
                        <div class="delivery-stat"><div class="delivery-stat__count text-danger">{{ engagementData.summary.notInterested }}</div><div class="delivery-stat__label">Not Interested</div></div>
                    </div>
                    <div *ngIf="engagementData.logs.length === 0" class="text-center text-muted py-4">No button clicks recorded yet.</div>
                    <div class="table-responsive" *ngIf="engagementData.logs.length > 0">
                        <table class="table table-sm table-hover align-middle mb-0">
                            <thead class="table-light">
                                <tr><th>Name</th><th>WhatsApp No.</th><th>Button Clicked</th><th>Clicked At</th></tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let log of engagementData.logs">
                                    <td>{{ log.recipientName }}</td>
                                    <td>{{ log.recipientNo }}</td>
                                    <td>
                                        <span class="badge"
                                            [ngClass]="{
                                                'bg-success': log.buttonText==='Interested',
                                                'bg-danger': log.buttonText==='Not Interested',
                                                'bg-primary': log.buttonText!=='Interested' && log.buttonText!=='Not Interested'
                                            }">{{ log.buttonText }}</span>
                                    </td>
                                    <td>{{ log.clickedAt | date:'dd MMM h:mm a' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ng-container>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
        </div>
    </div>
</div>

<!-- Recipients Modal -->
<div class="modal fade" id="triggerRecipientsModal" tabindex="-1" aria-hidden="true" #recipientsModal>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <div class="d-flex align-items-center gap-2">
                    <i class="pe-7s-users icon-gradient bg-happy-fisher" style="font-size:1.4rem;"></i>
                    <h5 class="modal-title mb-0">Recipients</h5>
                    <span class="text-muted fw-normal" style="font-size:0.85rem;">— {{ recipientsRunName }}</span>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body p-0" style="max-height:60vh;overflow-y:auto;">
                <div *ngIf="recipientsData.length === 0" class="text-center py-5 text-muted">No recipients found.</div>
                <table *ngIf="recipientsData.length > 0" class="table table-sm table-hover align-middle mb-0">
                    <thead class="table-light sticky-top">
                        <tr><th class="ps-3" style="width:48px">#</th><th>Name</th><th>WhatsApp No.</th></tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let r of recipientsData; let i = index">
                            <td class="ps-3 text-muted">{{ i+1 }}</td>
                            <td>{{ r.name }}</td>
                            <td>{{ r.whatsAppNo }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="modal-footer d-flex justify-content-between align-items-center">
                <span class="text-muted small">{{ recipientsData.length }} recipient{{ recipientsData.length === 1 ? '' : 's' }}</span>
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
        </div>
    </div>
</div>
